import { WPCF7FormField } from '@/types'

/**
 * Sentinel value used in select options to represent null.
 * When submitting form data, replace this value with null.
 */
export const NULL_SELECT_VALUE = '__NULL__'

export type RemoteSelectPrependOption = {
  label: string
  value: string | null
}

export type RemoteSelectConfig = {
  isRemote: boolean
  remoteKey: string | null
  dependsOn: string | null // field adminLabel (e.g., "country_id")
  prependOption: RemoteSelectPrependOption | null
}

/**
 * Parse CSS classes to extract remote select configuration
 * Classes:
 * - gf-remote: marks the field as remote
 * - gf-remote-key__xxx: the API key to use (e.g., "campus", "city", "university")
 * - gf-depends-on__xxx: the field adminLabel this select depends on (e.g., "country_id")
 * - gf-remote-prepend__label--value: prepend a fixed option before remote values (e.g., "gf-remote-prepend__Altro--other")
 *   If no "--" separator is present, value defaults to label (e.g., "gf-remote-prepend__Altro" → label: "Altro", value: "Altro")
 */
export const parseRemoteSelectConfig = (field: WPCF7FormField): RemoteSelectConfig => {
  const classes = (field.cssClass || '').split(' ').filter(Boolean)

  const isRemote = classes.includes('gf-remote')

  let remoteKey: string | null = null
  let dependsOn: string | null = null
  let prependOption: RemoteSelectPrependOption | null = null

  for (const cls of classes) {
    if (cls.startsWith('gf-remote-key__')) {
      remoteKey = cls.replace('gf-remote-key__', '')
    }
    if (cls.startsWith('gf-depends-on__')) {
      dependsOn = cls.replace('gf-depends-on__', '')
    }
    if (cls.startsWith('gf-remote-prepend__')) {
      const raw = cls.replace('gf-remote-prepend__', '')
      const parts = raw.split('--')
      const label = parts[0].replace(/_/g, ' ')
      const value =
        parts.length > 1 ? (parts[1] === 'null' ? null : parts[1].replace(/_/g, ' ')) : label
      prependOption = { label, value }
    }
  }

  return {
    isRemote,
    remoteKey,
    dependsOn,
    prependOption
  }
}

/**
 * Map remote keys to API endpoints
 */
export const REMOTE_KEY_ENDPOINTS: Record<string, string> = {
  campus: '/campuses',
  campuses: '/campuses',
  city: '/cities',
  cities: '/cities',
  country: '/countries',
  countries: '/countries',
  university: '/universities',
  universities: '/universities',
  course: '/courses',
  courses: '/courses',
  feature: '/features',
  features: '/features',
  services_included: '/services_included'
}

/**
 * Get the API endpoint for a remote key
 */
export const getRemoteEndpoint = (remoteKey: string): string | null => {
  return REMOTE_KEY_ENDPOINTS[remoteKey] || null
}

/**
 * Find the field ID by adminLabel
 */
export const findFieldIdByAdminLabel = (
  fields: WPCF7FormField[],
  adminLabel: string
): string | null => {
  const field = fields.find((f) => f.adminLabel === adminLabel)
  return field?.id || null
}

/**
 * Map dependency field names to API query parameters
 */
export const DEPENDENCY_PARAM_MAP: Record<string, string> = {
  country_id: 'country_id',
  city_id: 'city_id',
  university_id: 'university_id',
  campus_id: 'campus_id'
}

/**
 * Get the query parameter name for a dependency
 */
export const getDependencyParam = (dependsOn: string): string => {
  return DEPENDENCY_PARAM_MAP[dependsOn] || dependsOn
}
